import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import RDom from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import SM from 'services/ServiceManager';
import { getPeriodByFilter } from 'utils';
import { getDatesDifference } from 'utils/datetime';
import { formatWithDate } from 'utils/formatting';
import { YTD } from 'filters/investementPerformanceFilters';
import { LINE, RISKRETURN } from 'constants/constants';
import RiskReturnTooltip from 'components/RiskReturnTooltip';
import {
    adaptPlot,
    adaptRiskCompare,
} from 'adaptors/adaptPerformanceAndRiskCompare';
import { useFormatting } from '../locale';
import { CHART_DISPLAY_OPTION } from '../pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/constants';
import { SINCE_INCEPTION } from '../filters/investementPerformanceFilters';
import { formatDate } from '../utils/datetime';

const MAX_DAYS = 3651;

export const usePerformanceAndRiskCompare = ({
    clientId,
    portfolio,
    strategy,
    currencyId,
    benchmark,
    additionalOptions,
    isLoading,
}) => {
    const { isEdit, usePortfolioPerformance } = additionalOptions || {};

    const {
        t,
        i18n: { language },
    } = useTranslation();
    const { getFormattedNumber, getFormattedDate } = useFormatting();

    const [performanceTime, setPerformanceTime] = useState(YTD);
    const [displayOption, setDisplayOption] = useState(CHART_DISPLAY_OPTION.amount);
    const [performanceTimePortfolio, setPerformanceTimePortfolio] = useState(null);
    const [performanceTimeBenchmark, setPerformanceTimeBenchmark] = useState(null);
    const [performanceTimeForAdapter, setPerformanceTimeForAdapter] = useState(null);

    const onPerformanceChange = useCallback((param) => {
        setPerformanceTime(param);
        setPerformanceTimePortfolio(null);
        setPerformanceTimeForAdapter(null);
        setPerformanceTimeBenchmark(null);
    }, []);

    const onDisplayOptionChange = useCallback((payload) => {
        setDisplayOption(payload);
    }, []);

    // Portfolio query params
    const portfolioQueryParams = useMemo(() => {
        if (!usePortfolioPerformance || !portfolio?.id || !clientId || !language) return null;

        const periodDates = getPeriodByFilter(performanceTime);
        let from = periodDates.startDate;
        let to = periodDates.endDate;

        if (performanceTime === SINCE_INCEPTION && portfolio?.createDate) {
            const format = 'YYYY-MM-DD';
            const [day, month, year] = portfolio.createDate.split('.').map(Number);

            from = formatDate(new Date(year, month - 1, day), format);
            to = formatDate(new Date(), format);
        }

        if (!from && performanceTimeBenchmark) from = performanceTimeBenchmark;
        if (!from || !to) return null;

        return {
            portfolioId: portfolio.id,
            clientId,
            language,
            from,
            to,
        };
    }, [usePortfolioPerformance, portfolio, clientId, language, performanceTime, performanceTimeBenchmark]);

    const {
        data: portfolioRawData,
        isLoading: isLoadingPortfolio,
        error: portfolioError,
    } = useQuery({
        queryKey: ['portfolioDetailsPerformance', portfolioQueryParams],
        queryFn: () => SM.portfolioManagement('getPortfolioDetailsPerformance', [
            portfolioQueryParams!.portfolioId,
            portfolioQueryParams!.clientId,
            { language: portfolioQueryParams!.language, from: portfolioQueryParams!.from, to: portfolioQueryParams!.to },
            {},
        ]) as any,
        enabled: !!portfolioQueryParams,
        staleTime: 3 * 60 * 1000,
    });

    const portfolioData = useMemo(() => {
        const data = portfolioRawData?.data;

        return data ? { HistoricalPerformance: [data] } : null;
    }, [portfolioRawData]);

    const startDate = useMemo(() => {
        if (!portfolioData?.HistoricalPerformance?.length) return '';
        const sortedDates = portfolioData.HistoricalPerformance[0]?.Values?.map(
            (i) => i.Date,
        )?.sort((a, b) => (a > b ? 1 : -1));

        return sortedDates?.[0] || '';
    }, [portfolioData]);

    const endDate = useMemo(
        () => getPeriodByFilter(performanceTime).endDate,
        [performanceTime],
    );

    // Cascade: after portfolio loads, sync performanceTimePortfolio for strategy/benchmark
    useEffect(() => {
        if (!portfolioData) return;
        const periodStartDate = getPeriodByFilter(performanceTime).startDate;

        if (periodStartDate) return;
        const firstDPortfolio = portfolioData.HistoricalPerformance?.[0]?.Values?.[0]?.Date;

        if (
            firstDPortfolio
            && MAX_DAYS > getDatesDifference(new Date(), firstDPortfolio, 'days')
        ) {
            setPerformanceTimePortfolio(firstDPortfolio);
        }
    }, [portfolioData, performanceTime]);

    const setKPIParams = useCallback(
        (options, days?) => ({
            InstrumentSet: {
                ...options,
                CurrencyId: currencyId,
            },
            Days:
                (!startDate && days)
                || (startDate
                    ? getDatesDifference(new Date(), startDate, 'days')
                    : MAX_DAYS),
            CalculationInterval: 'Monthly',
        }),
        [startDate, currencyId],
    );

    // Strategy query params
    const strategyQueryParams = useMemo(() => {
        if (!strategy?.currencyId || !strategy?.investmentAllocations?.length || !currencyId) return null;

        const strategyStartDate = performanceTimePortfolio || performanceTimeBenchmark;
        const daysNew = strategyStartDate
            ? getDatesDifference(new Date(), strategyStartDate, 'days')
            : undefined;

        const options: any = {};

        if (strategy?.proposalId) {
            options.PortfolioProposalId = strategy.proposalId;
        } else if (isEdit) {
            options.Allocations = strategy?.investmentAllocations;
        } else {
            options.ModelPortfolioId = strategy?.modelPortfolioId;
        }

        return setKPIParams(options, daysNew);
    }, [strategy, currencyId, isEdit, setKPIParams, performanceTimePortfolio, performanceTimeBenchmark]);

    const {
        data: strategyRawData,
        isLoading: isLoadingStrategy,
    } = useQuery({
        queryKey: ['calculateKPIPerformance', 'strategy', strategyQueryParams],
        queryFn: () => SM.performanceService('calculateKPIPerformance', [strategyQueryParams]) as any,
        enabled: !!strategyQueryParams,
        staleTime: 3 * 60 * 1000,
    });

    const strategyData = useMemo(() => strategyRawData?.data ?? null, [strategyRawData]);

    // Benchmark query params
    const benchmarkQueryParams = useMemo(() => {
        if (!benchmark?.id) return null;

        const daysNew = performanceTimePortfolio
            ? getDatesDifference(new Date(), performanceTimePortfolio, 'days')
            : undefined;

        return setKPIParams(
            { Allocations: [{ InstrumentId: benchmark.id, Allocation: 1 }] },
            daysNew,
        );
    }, [benchmark?.id, setKPIParams, performanceTimePortfolio]);

    const {
        data: benchmarkRawData,
        isLoading: isLoadingBenchmarkQuery,
    } = useQuery({
        queryKey: ['calculateKPIPerformance', 'benchmark', benchmarkQueryParams],
        queryFn: () => SM.performanceService('calculateKPIPerformance', [benchmarkQueryParams]) as any,
        enabled: !!benchmarkQueryParams,
        staleTime: 3 * 60 * 1000,
    });

    const benchmarkData = useMemo(() => benchmarkRawData?.data ?? null, [benchmarkRawData]);

    const isLoadingBenchmark = benchmark?.id ? isLoadingBenchmarkQuery : isLoading;

    // Cascade: after benchmark loads, sync performanceTimeBenchmark for portfolio/strategy
    useEffect(() => {
        if (!benchmarkData || startDate) return;
        const firstDBenchmark = benchmarkData?.HistoricalPerformance?.[0]?.Values?.[0]?.Date;
        const daysNew = performanceTimePortfolio
            ? getDatesDifference(new Date(), performanceTimePortfolio, 'days')
            : undefined;

        if (
            firstDBenchmark
            && (daysNew || MAX_DAYS) > getDatesDifference(new Date(), firstDBenchmark, 'days')
        ) {
            setPerformanceTimeBenchmark(firstDBenchmark);
        }
    }, [benchmarkData, startDate, performanceTimePortfolio]);

    const valueSuffix = useMemo(
        () => (displayOption === CHART_DISPLAY_OPTION.percentage
            ? '%'
            : portfolioData?.HistoricalPerformance?.[0]?.Currency?.Symbol),
        [displayOption, portfolioData],
    );

    const yAxisFormat = useCallback(
        ({ value }) => `${getFormattedNumber(value, {
            maximumFractionDigits: 2,
        })}${valueSuffix}`,
        [getFormattedNumber, valueSuffix],
    );
    const tooltipFormat = useCallback(
        (number, date) => formatWithDate(
            `${getFormattedNumber(number, {
                maximumFractionDigits: 2,
            })}${valueSuffix}`,
            date,
            getFormattedDate,
        ),
        [getFormattedDate, getFormattedNumber, valueSuffix],
    );
    const tooltipFormatRisk = useCallback(
        (name, x, y) => RDom.renderToString(
            RiskReturnTooltip({
                x,
                y,
                t,
                getFormattedNumber,
            }),
        ),
        [getFormattedNumber, t],
    );

    const lineChartPortfolio = useMemo(
        () => adaptPlot(
            {
                ...portfolioData,
                startDate,
                endDate,
            },
            false,
            displayOption,
        ),
        [portfolioData, startDate, endDate, displayOption],
    );
    const lineChartStrategy = useMemo(
        () => adaptPlot({
            ...strategyData,
            startDate: startDate || performanceTimeForAdapter,
            endDate,
        }),
        [
            strategyData,
            performanceTimeForAdapter,
            startDate,
            endDate,
        ],
    );
    const lineChartBenchmark = useMemo(
        () => adaptPlot({
            ...benchmarkData,
            startDate: startDate || performanceTimeForAdapter,
            endDate,
        }),
        [
            benchmarkData,
            performanceTimeForAdapter,
            startDate,
            endDate,
        ],
    );
    const lineChart = useMemo(() => {
        const data: any = [
            {
                name:
                    portfolio?.performanceTitle
                    || t('clientDashboard.portfolio.current'),
                data: lineChartPortfolio?.data,
            },
        ];

        if (benchmark) {
            data.push({
                type: 'line',
                name: benchmark?.name || '',
                data: lineChartBenchmark?.data,
            });
        }

        if (strategy) {
            data.splice(1, 0, {
                type: 'line',
                name:
                    strategy?.performanceTitle
                    || t('clientDashboard.portfolio.new'),
                data: lineChartStrategy?.data,
            });
        }

        return { data, optional: { tooltipFormat, yAxisFormat } };
    }, [
        portfolio?.performanceTitle,
        strategy,
        benchmark?.name,
        lineChartPortfolio,
        lineChartStrategy,
        lineChartBenchmark,
        tooltipFormat,
        yAxisFormat,
    ]);
    const riskReturn = useMemo(
        () => adaptRiskCompare(portfolio, strategyData, {
            tooltipFormat: tooltipFormatRisk,
            t,
            getFormattedNumber,
            benchmark,
            strategyTitle: strategy?.riskReturnTitle,
            matrix:
                    strategy?.riskReturnMatrix || portfolio?.riskReturnMatrix,
        }),
        [
            portfolio,
            strategyData,
            strategy?.riskReturnTitle,
            strategy?.riskReturnMatrix,
            tooltipFormatRisk,
            t,
            getFormattedNumber,
            benchmark,
        ],
    );

    const volatility = useMemo(
        () => strategyData?.Volatility,
        [strategyData],
    );

    const error = portfolioError ?? null;
    const overallLoading = isLoadingPortfolio || isLoadingStrategy || isLoadingBenchmark;

    return {
        performanceAndRisk: {
            [LINE]: {
                ...lineChart,
                error,
                isLoading: overallLoading,
            },
            [RISKRETURN]: {
                ...riskReturn,
                error,
                isLoading: isLoadingBenchmarkQuery || isLoadingStrategy,
            },
        },
        errorPerformanceAndRisk: error,
        isLoadingPerformanceAndRisk: overallLoading,
        volatility,
        performanceSelected: performanceTime,
        displayOptionSelected: displayOption,
        onPerformanceChange,
        onDisplayOptionChange,
    };
};
